// src/components/managed-sources/VerityPixelStorefrontCard.tsx
/**
 * Links a Verity Pixel site to a Shopify storefront (plan 2026-10-04 campaign
 * exposure, step 7a) and shows whether Verity's web pixel is on that store.
 *
 * Linking does two things for the store's campaigns: new A/B tests can split
 * visitors on the pixel's visitor id, and the web pixel links checkouts
 * (express checkouts included) to the campaign a visitor saw.
 *
 * Renders nothing for an account without a Shopify storefront and no link:
 * there is nothing to choose. The live web pixel check (`GET …/web-pixel`)
 * runs once per linked storefront, never on a timer: it is a Shopify call.
 *
 * Page tracking (plan 2026-10-09 pixel app embed): the page script reaches a
 * Shopify store through the Verity Pixel app embed, which only the merchant
 * can switch on in the theme editor. The row reads its state
 * (`GET …/storefront-embed`, once per linked storefront, `fresh` on
 * **Check again**), deep-links an admin into the theme editor to switch it
 * on, and re-writes the store's config on **Fix**.
 */
import { useEffect, useRef, useState } from "react";
import { Alert, Anchor, Badge, Button, Group, Paper, Select, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons-react";

import { listFrontends, type FrontendDTO } from "@/api/frontends";
import {
  getStorefrontEmbed,
  getWebPixelState,
  installWebPixel,
  repairStorefrontEmbed,
  updatePixelSite,
  type PixelSite,
  type StorefrontEmbedState,
  type StorefrontEmbedStatus,
  type WebPixelState,
} from "@/api/pixelSites";
import { DocsLink } from "@/components/docs/DocsLink";

export const WEB_PIXEL_LABELS: Record<WebPixelState, string> = {
  installed: "Linking checkouts",
  not_installed: "Not installed",
  reconnect_required: "Needs checkout access",
  not_connected: "Store not connected",
  unavailable: "Not available yet",
  error: "Could not check",
  not_linked: "Not linked",
};

const WEB_PIXEL_COLORS: Record<WebPixelState, string> = {
  installed: "green",
  not_installed: "yellow",
  reconnect_required: "yellow",
  not_connected: "orange",
  unavailable: "gray",
  error: "red",
  not_linked: "gray",
};

/** What the customer can do about each state. */
const WEB_PIXEL_HELP: Partial<Record<WebPixelState, string>> = {
  not_installed: "Verity can add its checkout pixel to this store now.",
  reconnect_required:
    "Give Verity checkout access: open Data Management → Authorizations, open this store's Shopify connection and click Grant checkout access. Then come back and click Install.",
  not_connected:
    "Connect this Shopify store under Data Management → Authorizations first.",
  unavailable: "Checkout linking is not available in this environment yet.",
  error: "Shopify did not answer. Try again in a minute.",
};

export const PAGE_TRACKING_LABELS: Record<StorefrontEmbedState, string> = {
  on: "On",
  off: "Off",
  stale_config: "Needs an update",
  other_site: "Other workspace",
  reconnect_required: "Needs store access",
  not_connected: "Store not connected",
  unavailable: "Not available yet",
  error: "Could not check",
  not_linked: "Not linked",
};

const PAGE_TRACKING_COLORS: Record<StorefrontEmbedState, string> = {
  on: "green",
  off: "yellow",
  stale_config: "yellow",
  other_site: "gray",
  reconnect_required: "yellow",
  not_connected: "orange",
  unavailable: "gray",
  error: "red",
  not_linked: "gray",
};

/** What each page-tracking state means for the customer (`off` is rendered separately). */
const PAGE_TRACKING_HELP: Partial<Record<StorefrontEmbedState, string>> = {
  stale_config: "Verity's page tracking settings on this store are out of date.",
  error: "Verity could not check page tracking on this store. Try again in a minute.",
  reconnect_required:
    "Give Verity access to this store again: open Data Management → Authorizations, open this store's Shopify connection and reconnect it. Then come back and click Check again.",
  other_site: "This store sends its page views to another Verity workspace.",
  not_connected:
    "Connect this Shopify store under Data Management → Authorizations first.",
  unavailable: "Page tracking is not available in this environment yet.",
};

const NONE = "";

type Props = {
  site: PixelSite;
  isAccountAdmin: boolean;
  /** Called with the saved site after a link change. */
  onSiteChange: (site: PixelSite) => void;
};

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

export default function VerityPixelStorefrontCard({ site, isAccountAdmin, onSiteChange }: Props) {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const [storefronts, setStorefronts] = useState<FrontendDTO[] | null>(null);
  const [choice, setChoice] = useState<string>(site.storefront_id ?? NONE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pixel, setPixel] = useState<WebPixelState | null>(null);
  const [checking, setChecking] = useState(false);
  const [installing, setInstalling] = useState(false);
  // The answer is keyed by the link it was read for, so a link change shows
  // "Checking…" without resetting state inside an effect.
  const [embedResult, setEmbedResult] = useState<{ link: string; status: StorefrontEmbedStatus } | null>(null);
  const [rechecking, setRechecking] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [embedError, setEmbedError] = useState<string | null>(null);

  useEffect(() => {
    listFrontends()
      .then((all) => {
        if (mounted.current) setStorefronts(all.filter((f) => f.kind === "SHOPIFY_STOREFRONT"));
      })
      .catch(() => {
        if (mounted.current) setStorefronts([]);
      });
  }, []);

  useEffect(() => {
    setChoice(site.storefront_id ?? NONE);
  }, [site.storefront_id]);

  // One live check per linked storefront. A PATCH that just linked already
  // carries the answer, so it is used instead of a second Shopify call.
  useEffect(() => {
    if (!site.storefront_id) {
      setPixel(null);
      return;
    }
    if (site.web_pixel) {
      setPixel(site.web_pixel);
      return;
    }
    setChecking(true);
    getWebPixelState(site.site_key)
      .then((s) => {
        if (mounted.current) setPixel(s);
      })
      .catch(() => {
        if (mounted.current) setPixel("error");
      })
      .finally(() => {
        if (mounted.current) setChecking(false);
      });
  }, [site.site_key, site.storefront_id, site.web_pixel]);

  const link = site.storefront_id ? `${site.site_key}:${site.storefront_id}` : null;
  const embed = link && embedResult?.link === link ? embedResult.status : null;

  function readEmbed(siteKey: string, forLink: string, fresh: boolean): Promise<void> {
    return getStorefrontEmbed(siteKey, { fresh })
      .catch((): StorefrontEmbedStatus => ({ state: "error", activate_url: null, missing_hosts: [] }))
      .then((status) => {
        if (mounted.current) setEmbedResult({ link: forLink, status });
      });
  }

  // Page tracking: one read per linked storefront (a Shopify call, never on a
  // timer). A PATCH's `storefront_embed` is a write result, not this state.
  useEffect(() => {
    if (!link) return;
    void readEmbed(site.site_key, link, false);
  }, [site.site_key, link]);

  async function handleCheckAgain() {
    if (!link) return;
    setRechecking(true);
    setEmbedError(null);
    await readEmbed(site.site_key, link, true);
    if (mounted.current) setRechecking(false);
  }

  async function handleRepair() {
    setRepairing(true);
    setEmbedError(null);
    try {
      const status = await repairStorefrontEmbed(site.site_key);
      if (mounted.current && link) setEmbedResult({ link, status });
    } catch (e) {
      if (mounted.current) setEmbedError(errorMessage(e, "Could not update page tracking on this store."));
    } finally {
      if (mounted.current) setRepairing(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const updated = await updatePixelSite(site.site_key, {
        storefront_id: choice === NONE ? null : choice,
      });
      if (!mounted.current) return;
      onSiteChange(updated);
      notifications.show({
        title: updated.storefront_id ? "Storefront linked" : "Storefront unlinked",
        message: updated.storefront_id
          ? `This pixel now measures ${updated.storefront_name ?? "the storefront"}.`
          : "Checkouts on that store are no longer linked to this pixel.",
        color: "green",
      });
    } catch (e) {
      if (mounted.current) setError(errorMessage(e, "Could not change the storefront."));
    } finally {
      if (mounted.current) setSaving(false);
    }
  }

  async function handleInstall() {
    setInstalling(true);
    try {
      const s = await installWebPixel(site.site_key);
      if (mounted.current) setPixel(s);
    } catch (e) {
      if (mounted.current) setError(errorMessage(e, "Could not install the checkout pixel."));
    } finally {
      if (mounted.current) setInstalling(false);
    }
  }

  if (storefronts === null) return null;
  if (storefronts.length === 0 && !site.storefront_id) return null;

  const options = [
    { value: NONE, label: "Not linked" },
    ...storefronts.map((f) => ({ value: f.id, label: f.name })),
  ];
  // A linked storefront the list no longer returns still shows by name.
  if (site.storefront_id && !storefronts.some((f) => f.id === site.storefront_id)) {
    options.push({ value: site.storefront_id, label: site.storefront_name ?? site.storefront_id });
  }
  const dirty = choice !== (site.storefront_id ?? NONE);
  const embedChecking = rechecking || (link !== null && embed === null);
  const embedState = embed?.state ?? null;
  const canRepair =
    isAccountAdmin && !dirty && (embedState === "stale_config" || embedState === "error");
  const missingHosts = embed?.missing_hosts ?? [];
  const canInstall =
    isAccountAdmin && !dirty && (pixel === "not_installed" || pixel === "error" || pixel === "reconnect_required");

  return (
    <Paper withBorder radius="md" p="md" data-testid="pixel-storefront-card">
      <Stack gap="sm">
        <Text fw={600} size="sm">Shopify storefront</Text>
        <Text size="sm" c="dimmed">
          Link the Shopify store this pixel runs on. Its campaigns can then split visitors
          on the pixel, and Verity links checkouts, including express checkouts, to the
          campaign a visitor saw.
        </Text>
        <DocsLink slug="activation/campaigns/building/testing-your-campaign">How campaign results are measured</DocsLink>

        {isAccountAdmin ? (
          <Group align="flex-end" gap="sm">
            <Select
              label="Storefront"
              data={options}
              value={choice}
              onChange={(v) => setChoice(v ?? NONE)}
              allowDeselect={false}
              w={280}
            />
            <Button onClick={handleSave} loading={saving} disabled={!dirty}>
              {choice === NONE && site.storefront_id ? "Unlink" : "Link storefront"}
            </Button>
          </Group>
        ) : (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Storefront</Text>
            <Text size="sm" fw={500}>{site.storefront_name ?? "Not linked"}</Text>
          </Group>
        )}

        {site.storefront_id && (
          <Stack gap={6}>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Checkout pixel</Text>
              {checking ? (
                <Text size="sm" c="dimmed">Checking…</Text>
              ) : pixel ? (
                <Badge variant="light" color={WEB_PIXEL_COLORS[pixel]} data-testid="web-pixel-badge">
                  {WEB_PIXEL_LABELS[pixel]}
                </Badge>
              ) : null}
            </Group>
            {pixel && WEB_PIXEL_HELP[pixel] && (
              <Text size="xs" c="dimmed" data-testid="web-pixel-help">{WEB_PIXEL_HELP[pixel]}</Text>
            )}
            {canInstall && (
              <Group justify="flex-end">
                <Button size="xs" variant="light" onClick={handleInstall} loading={installing}>
                  Install
                </Button>
              </Group>
            )}
          </Stack>
        )}

        {site.storefront_id && (
          <Stack gap={6} data-testid="page-tracking-row">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Page tracking</Text>
              {embedChecking ? (
                <Text size="sm" c="dimmed">Checking…</Text>
              ) : embedState ? (
                <Badge variant="light" color={PAGE_TRACKING_COLORS[embedState]} data-testid="page-tracking-badge">
                  {PAGE_TRACKING_LABELS[embedState]}
                </Badge>
              ) : null}
            </Group>
            {!embedChecking && embedState === "off" && (
              <Text size="xs" c="dimmed" data-testid="page-tracking-help">
                {!isAccountAdmin ? (
                  "Page tracking is off on this store. An account admin can turn it on in Shopify."
                ) : embed?.activate_url ? (
                  "Shopify asks you to switch on Verity Pixel under App embeds, then click Save."
                ) : (
                  <>
                    In Shopify, open <b>Online Store → Themes → Customize → App embeds</b>,
                    switch on <b>Verity Pixel</b> and click <b>Save</b>.
                  </>
                )}
              </Text>
            )}
            {!embedChecking && embedState && PAGE_TRACKING_HELP[embedState] && (
              <Text size="xs" c="dimmed" data-testid="page-tracking-help">
                {PAGE_TRACKING_HELP[embedState]}
              </Text>
            )}
            {!embedChecking && missingHosts.length > 0 && (
              <Alert color="yellow" icon={<IconAlertCircle size={16} />} data-testid="page-tracking-missing-hosts">
                Visits on {missingHosts.join(", ")} are not recorded: add them under Allowed domains
                in Pixel settings, or make sure your store redirects them to your website address.
              </Alert>
            )}
            {embedError && (
              <Text size="xs" c="red" data-testid="page-tracking-error">{embedError}</Text>
            )}
            <Group justify="flex-end" gap="sm">
              <Anchor
                component="button"
                type="button"
                size="xs"
                onClick={handleCheckAgain}
                disabled={embedChecking}
              >
                Check again
              </Anchor>
              {isAccountAdmin && !dirty && embedState === "off" && embed?.activate_url && (
                <Button
                  size="xs"
                  component="a"
                  href={embed.activate_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Turn on in Shopify
                </Button>
              )}
              {canRepair && (
                <Button size="xs" variant="light" onClick={handleRepair} loading={repairing}>
                  Fix
                </Button>
              )}
            </Group>
          </Stack>
        )}

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Storefront not changed">
            {error}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
